import { jsx as _jsx } from "react/jsx-runtime";
// Team Chat state: context bootstrap, hub lifecycle, presence, unread, and the
// cache-patching bridge from hub events into TanStack Query. The hub connection
// itself is the teamHub module singleton (StrictMode-safe); this provider only
// decides WHEN it should be running.
import { useAuth } from '@/hooks/use-auth';
import { createTeamChannel, createTeamDm, getOrCreateCaseThread, getTeamContext, getTeamMessages, getTeamThread, markTeamConversationRead, sendTeamMessage, } from '@/lib/api/team';
import { queryClient } from '@/lib/queryClient';
import { collabSession } from '@/lib/team/collabSession';
import { TEAM_CONTEXT_KEY, TEAM_MESSAGES_KEY_PREFIX, TEAM_THREAD_KEY_PREFIX, teamMessagesKey, teamThreadKey, } from '@/lib/team/queryKeys';
import { patchReactionCaches } from '@/lib/team/reactionCache';
import { teamHub } from '@/lib/team/teamHub';
import { recordTeamHubDiag } from '@/lib/team/teamHubDiagnostics';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
const EMPTY_CONTEXT = { userId: '', groups: [], members: [], conversations: [] };
/** Chat users: the roster (case counts, members) stays reasonably live. */
const CHAT_CONTEXT_STALE_MS = 60_000;
/** No chat-enabled group (the default): nothing to keep live, so no refetch on
 * every window focus — being added to a group is picked up on the next load. */
const NO_CHAT_CONTEXT_STALE_MS = 30 * 60_000;
/** Backoff for restarting the hub once SignalR's own automatic reconnect has
 * given up (or the first start failed); the last value repeats. */
const HUB_RESTART_DELAYS_MS = [2_000, 5_000, 10_000, 30_000, 60_000];
/** caseCountsChanged pushes coalesce into one roster refetch per window. */
const CASE_COUNTS_REFETCH_DELAY_MS = 2_000;
/** Diagnostic heartbeat cadence: snapshots the receive path while connected (see teamHubDiagnostics). */
const TEAM_HUB_HEARTBEAT_MS = 15_000;
/** Active liveness-probe cadence while connected: catch a socket that went deaf without a
 * visibility change, and keep a foregrounded tab's connection warm (a Ping is a client message, so
 * the server never reaps it). ~15 s, matching the server's keep-alive. */
const TEAM_HUB_LIVENESS_MS = 15_000;
function hasChatGroups(data) {
    return (data?.groups.length ?? 0) > 0;
}
const TeamChatReactContext = createContext(null);
const TeamChatViewerContext = createContext(null);
// eslint-disable-next-line react-refresh/only-export-components
export function useTeamChat() {
    const value = useContext(TeamChatReactContext);
    if (!value)
        throw new Error('useTeamChat must be used within a TeamChatProvider');
    return value;
}
/** Optional variant for surfaces that can do without chat (viewer dialogs, the
 * case panel). App.tsx wraps every route, the public share view included, in
 * TeamChatProvider, so the null branch exists for tests that render them alone. */
// eslint-disable-next-line react-refresh/only-export-components
export function useTeamChatOptional() {
    return useContext(TeamChatReactContext);
}
/** Narrow, render-stable subscription for the viewer (see TeamChatViewerInfo);
 * null outside a TeamChatProvider. */
// eslint-disable-next-line react-refresh/only-export-components
export function useTeamChatViewer() {
    return useContext(TeamChatViewerContext);
}
function patchContext(patch) {
    queryClient.setQueryData([...TEAM_CONTEXT_KEY], (prev) => prev ? patch(prev) : prev);
}
/** Prepend a conversation the caller just created/opened (or that the hub
 * pushed) unless it is already listed. Nothing to subscribe to: the chat service
 * addresses each event to the conversation's readers when it publishes it. */
function adoptConversation(conversation) {
    patchContext((ctx) => ctx.conversations.some((c) => c.id === conversation.id)
        ? ctx
        : { ...ctx, conversations: [conversation, ...ctx.conversations] });
}
/** Zero a conversation's unread locally and tell the server — when it has any. */
function markReadIfUnread(conversationId) {
    const conversation = queryClient
        .getQueryData([...TEAM_CONTEXT_KEY])
        ?.conversations.find((c) => c.id === conversationId);
    if (!conversation || conversation.unreadCount === 0)
        return;
    patchContext((ctx) => ({
        ...ctx,
        conversations: ctx.conversations.map((c) => c.id === conversationId ? { ...c, unreadCount: 0 } : c),
    }));
    void markTeamConversationRead(conversationId).catch(() => { });
}
/** Folds a freshly fetched newest page into a cached conversation: older
 * loaded pages and the cursor stay; messages in both take the fresh copy
 * (edits, reaction and reply counts); cached messages inside the fresh window
 * that are gone were deleted while offline. No overlap means a gap the cache
 * cannot bridge, so the fresh page replaces it. */
function mergeNewestPage(prev, fresh) {
    const firstFresh = fresh.messages[0];
    if (!firstFresh)
        return prev.messages.length === 0 ? fresh : prev;
    const overlapAt = prev.messages.findIndex((m) => m.id === firstFresh.id);
    if (overlapAt < 0) {
        const anyShared = prev.messages.some((m) => fresh.messages.some((f) => f.id === m.id));
        if (!anyShared)
            return fresh;
    }
    const freshById = new Map(fresh.messages.map((m) => [m.id, m]));
    const keep = overlapAt >= 0 ? prev.messages.slice(0, overlapAt) : prev.messages;
    const merged = keep.map((m) => freshById.get(m.id) ?? m);
    const seen = new Set(merged.map((m) => m.id));
    for (const m of fresh.messages)
        if (!seen.has(m.id))
            merged.push(m);
    return { hasMore: prev.hasMore, messages: merged };
}
/** Fetches a conversation's newest page and merges it into the cache (see mergeNewestPage). */
function refreshNewestPage(conversationId) {
    void getTeamMessages(conversationId)
        .then((fresh) => {
        queryClient.setQueryData(teamMessagesKey(conversationId), (prev) => prev ? mergeNewestPage(prev, fresh) : fresh);
    })
        .catch(() => {
        // Best effort: the next mount refetches once the entry is stale.
        void queryClient.invalidateQueries({ queryKey: teamMessagesKey(conversationId) });
    });
}
function reconcileCachedConversations() {
    for (const [key, data] of queryClient.getQueriesData({
        queryKey: [...TEAM_MESSAGES_KEY_PREFIX],
    })) {
        const conversationId = key[1];
        if (!data || typeof conversationId !== 'string')
            continue;
        refreshNewestPage(conversationId);
    }
}
/** Who is online, as the roster reports it: the chat service puts each member's connection
 * state and chosen status on GET /chat/context (there is no snapshot on connect). */
function presenceFromMembers(members) {
    const presence = {};
    for (const member of members) {
        if (member.isOnline && member.status !== 'offline')
            presence[member.userId] = member.status;
    }
    return presence;
}
function previewOf(message) {
    return message.kind === 'text' ? message.body.slice(0, 80) : message.kind;
}
export function TeamChatProvider({ children }) {
    const { isAuthenticated } = useAuth();
    const [connectionState, setConnectionState] = useState(teamHub.getState());
    const [presence, setPresence] = useState({});
    const [panelOpen, setPanelOpen] = useState(false);
    const [activeConversationId, setActiveConversationId] = useState(null);
    const [typing, setTyping] = useState({});
    const contextQuery = useQuery({
        queryKey: [...TEAM_CONTEXT_KEY],
        queryFn: getTeamContext,
        enabled: isAuthenticated,
        staleTime: (query) => hasChatGroups(query.state.data) ? CHAT_CONTEXT_STALE_MS : NO_CHAT_CONTEXT_STALE_MS,
        refetchOnWindowFocus: (query) => hasChatGroups(query.state.data),
    });
    const context = contextQuery.data ?? EMPTY_CONTEXT;
    const enabled = context.groups.length > 0;
    // Presence seeds from the roster each time a fresh one arrives (first load, the reconcile
    // refetch after a reconnect, a case-count refetch); presenceChanged events keep it live in
    // between. Local patches of the context keep the members array, so they never reseed.
    const [seededMembers, setSeededMembers] = useState(null);
    if (seededMembers !== context.members) {
        setSeededMembers(context.members);
        setPresence(presenceFromMembers(context.members));
    }
    // Logout. This provider is mounted once in App and never remounts, so the
    // next user to sign in on this tab inherited the open panel, conversation,
    // typing and presence (reset here during render, the React pattern for
    // state that follows a prop) and the collabSession singleton (effect below):
    // presenter mode survives navigation by design, so user B saw user A's
    // "Live session active" pill and its End control.
    const [wasAuthenticated, setWasAuthenticated] = useState(isAuthenticated);
    if (wasAuthenticated !== isAuthenticated) {
        setWasAuthenticated(isAuthenticated);
        if (!isAuthenticated) {
            setPanelOpen(false);
            setActiveConversationId(null);
            setTyping({});
            setPresence({});
        }
    }
    const signedInRef = useRef(isAuthenticated);
    useEffect(() => {
        if (signedInRef.current && !isAuthenticated)
            collabSession.stop();
        signedInRef.current = isAuthenticated;
    }, [isAuthenticated]);
    // Own status DERIVES from the presence map — the server echoes a status change
    // to the whole roster (caller included) and the roster includes self, so
    // presence[self] is authoritative. A separate copy went stale on reconnect,
    // showing the pre-drop status to its owner alone.
    const myStatus = presence[context.userId] ?? 'online';
    // Refs so hub handlers read live state without resubscribing (the
    // use-auth logoutRef pattern: synced in an effect, read in callbacks).
    const activeConvRef = useRef(activeConversationId);
    const panelOpenRef = useRef(panelOpen);
    const selfIdRef = useRef(context.userId);
    useEffect(() => {
        activeConvRef.current = activeConversationId;
        panelOpenRef.current = panelOpen;
        selfIdRef.current = context.userId;
    });
    // The chosen status the chat service last confirmed: what an optimistic change falls back to
    // when the service never saved it. Seeded from each fresh roster, then kept by own-status
    // echoes and confirmed writes.
    const persistedStatusRef = useRef('online');
    useEffect(() => {
        const self = context.members.find((m) => m.userId === context.userId);
        if (self && self.status !== 'offline')
            persistedStatusRef.current = self.status;
    }, [context.members, context.userId]);
    // Hub lifecycle: run while authenticated AND at least one chat-enabled group.
    // SignalR's automatic reconnect gives up after its last delay, and a failed
    // first start never retries by itself — so whenever the hub falls back to
    // 'idle' while it should be running (laptop sleep, Wi-Fi loss, a deploy),
    // restart it with capped backoff, and right away when the browser comes back
    // online or the tab becomes visible again.
    useEffect(() => {
        if (!isAuthenticated || !enabled) {
            void teamHub.stop();
            return;
        }
        let cancelled = false;
        let attempt = 0;
        let restartTimer = null;
        const clearRestart = () => {
            if (restartTimer !== null)
                clearTimeout(restartTimer);
            restartTimer = null;
        };
        const start = () => {
            clearRestart();
            recordTeamHubDiag('provider:start', { attempt });
            // A failed start lands in 'idle', which schedules the next attempt.
            void teamHub.ensureStarted().catch(() => { });
        };
        const scheduleRestart = () => {
            if (cancelled || restartTimer !== null)
                return;
            // Refused for holding too many connections (other tabs/windows): restarting on a timer
            // would only be refused again. The tab shows offline; becoming visible or coming back
            // online (retryNow) still tries once.
            if (teamHub.isOverConnectionLimit()) {
                recordTeamHubDiag('provider:scheduleRestart:skip', { reason: 'over_connection_limit' });
                return;
            }
            const delay = HUB_RESTART_DELAYS_MS[Math.min(attempt, HUB_RESTART_DELAYS_MS.length - 1)];
            recordTeamHubDiag('provider:scheduleRestart', { attempt, delay });
            attempt += 1;
            restartTimer = setTimeout(() => {
                restartTimer = null;
                if (!cancelled)
                    start();
            }, delay);
        };
        const retryNow = (trigger) => {
            if (cancelled || teamHub.getState() !== 'idle') {
                recordTeamHubDiag('provider:retryNow:skip', { trigger, state: teamHub.getState() });
                return;
            }
            recordTeamHubDiag('provider:retryNow', { trigger });
            attempt = 0;
            start();
        };
        // The idle-restart loop (retryNow) only ever fires when the hub is already 'idle'. The nastier
        // failure is a socket that still reports 'connected' but the service reaped it while the tab was
        // backgrounded: retryNow skips it ("state is connected"), and the tab sits deaf. So, separately,
        // actively verify a 'connected' socket with a server round-trip and force a reconnect when it
        // does not answer — which is what breaks that confirmed-dead socket out of the retry-skip trap.
        const verifyLivenessAndRecover = async (trigger) => {
            if (cancelled || teamHub.getState() !== 'connected')
                return;
            const result = await teamHub.verifyLiveness();
            if (cancelled || result !== 'dead')
                return;
            recordTeamHubDiag('provider:livenessRecover', { trigger });
            attempt = 0;
            await teamHub.forceReconnect(`liveness:${trigger}`);
        };
        const onOnline = () => retryNow('online');
        const onVisibilityChange = () => {
            if (document.visibilityState !== 'visible')
                return;
            // Idle → restart now; connected-but-maybe-dead → probe and reconnect if it does not answer.
            retryNow('visible');
            void verifyLivenessAndRecover('visible');
        };
        const unsubscribe = teamHub.on('connectionStateChanged', (state) => {
            if (state === 'idle')
                scheduleRestart();
            else if (state === 'connected') {
                attempt = 0;
                clearRestart();
            }
        });
        window.addEventListener('online', onOnline);
        document.addEventListener('visibilitychange', onVisibilityChange);
        const livenessTimer = setInterval(() => void verifyLivenessAndRecover('periodic'), TEAM_HUB_LIVENESS_MS);
        start();
        return () => {
            // Only stop on auth/enabled flips (handled above) or provider unmount.
            cancelled = true;
            unsubscribe();
            clearRestart();
            clearInterval(livenessTimer);
            window.removeEventListener('online', onOnline);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            void teamHub.stop();
        };
    }, [isAuthenticated, enabled]);
    // Diagnostic heartbeat: while the hub reports Connected, snapshot the receive-path health every
    // 15 s (lastSeq, held count, ms since the last applied event) into the diagnostics timeline, so
    // a "connected but deaf" tab is visible even though nothing threw. Read-only; cleared on unmount.
    useEffect(() => {
        const interval = setInterval(() => {
            if (teamHub.getState() !== 'connected')
                return;
            recordTeamHubDiag('heartbeat', teamHub.diagnostics());
        }, TEAM_HUB_HEARTBEAT_MS);
        return () => clearInterval(interval);
    }, []);
    // Hub event wiring (once).
    useEffect(() => {
        let caseCountsTimer = null;
        let presenceCleared = false;
        const subs = [
            teamHub.on('connectionStateChanged', (state) => {
                setConnectionState(state);
                if (state === 'idle') {
                    // Disconnected: presence is unknowable — everyone renders offline
                    // until the roster is fetched again on the next connect.
                    setPresence({});
                    presenceCleared = true;
                }
                if (state === 'connected' && presenceCleared) {
                    // Replayed events carry presence CHANGES, not who is online: reseed it from the
                    // roster. (Messages and the rest come back through resume, or `resync` below.)
                    presenceCleared = false;
                    void queryClient.invalidateQueries({ queryKey: [...TEAM_CONTEXT_KEY] });
                }
            }),
            teamHub.on('resync', () => {
                // The connection could not replay what it missed (first connect, a long sleep, a
                // lost buffer): reload from the chat service — the roster, every cached
                // conversation page (merged, so loaded older pages survive) and thread (capped,
                // fully loaded: plain refetch; only mounted ones refetch, the rest are marked
                // stale). A reconnect that replayed everything needs none of this.
                recordTeamHubDiag('provider:resync', {});
                void queryClient.invalidateQueries({ queryKey: [...TEAM_CONTEXT_KEY] });
                reconcileCachedConversations();
                void queryClient.invalidateQueries({ queryKey: [...TEAM_THREAD_KEY_PREFIX] });
            }),
            teamHub.on('presenceChanged', (event) => {
                if (event.userId === selfIdRef.current && event.status !== 'offline') {
                    persistedStatusRef.current = event.status;
                }
                setPresence((prev) => {
                    const next = { ...prev };
                    if (event.status === 'offline')
                        delete next[event.userId];
                    else
                        next[event.userId] = event.status;
                    return next;
                });
            }),
            teamHub.on('caseCountsChanged', () => {
                // Recount-on-demand: the roster query recomputes counts; never track
                // increments client-side (assignment and status change in many backend
                // places, each of which pings through TeamEvents). A busy group pings
                // in bursts, so pushes within one window share a single refetch.
                if (caseCountsTimer !== null)
                    return;
                caseCountsTimer = setTimeout(() => {
                    caseCountsTimer = null;
                    void queryClient.invalidateQueries({ queryKey: [...TEAM_CONTEXT_KEY] });
                }, CASE_COUNTS_REFETCH_DELAY_MS);
            }),
            teamHub.on('messageReceived', (message) => {
                const isOwn = message.senderId === selfIdRef.current;
                const isOpen = activeConvRef.current === message.conversationId && panelOpenRef.current;
                // Too large for the real-time service: the event carries no body, and there is no
                // single-message GET, so the page (and the thread) are fetched instead of inserting it.
                const bodyOmitted = message.bodyOmitted === true;
                if (message.replyToId) {
                    // Thread replies never join the top-level list and never count as
                    // unread or as the conversation preview (the server's unread counts
                    // exclude them too): bump the parent's reply count and append into
                    // the open thread cache, nothing else.
                    const parentId = message.replyToId;
                    // Idempotent: a reply the cached thread already holds is already counted (a
                    // `resync` reload can fetch it before its live event lands). Without a cached
                    // thread there is nothing to check against, so the bump applies.
                    const thread = queryClient.getQueryData(teamThreadKey(parentId));
                    const alreadyApplied = thread?.messages.some((m) => m.id === message.id) ?? false;
                    if (!alreadyApplied) {
                        queryClient.setQueryData(teamMessagesKey(message.conversationId), (prev) => prev
                            ? {
                                ...prev,
                                messages: prev.messages.map((m) => m.id === parentId ? { ...m, replyCount: m.replyCount + 1 } : m),
                            }
                            : prev);
                    }
                    if (bodyOmitted) {
                        void queryClient.invalidateQueries({ queryKey: teamThreadKey(parentId) });
                    }
                    else {
                        queryClient.setQueryData(teamThreadKey(parentId), (prev) => {
                            if (!prev || prev.messages.some((m) => m.id === message.id))
                                return prev;
                            return { ...prev, messages: [...prev.messages, message] };
                        });
                    }
                }
                else {
                    // Idempotent: a message the cached page already holds is already counted (a
                    // `resync` reload can fetch it before its live event lands).
                    const alreadyApplied = queryClient
                        .getQueryData(teamMessagesKey(message.conversationId))
                        ?.messages.some((m) => m.id === message.id) ?? false;
                    if (bodyOmitted) {
                        if (queryClient.getQueryData(teamMessagesKey(message.conversationId))) {
                            refreshNewestPage(message.conversationId);
                        }
                    }
                    else {
                        queryClient.setQueryData(teamMessagesKey(message.conversationId), (prev) => {
                            if (!prev)
                                return prev;
                            if (prev.messages.some((m) => m.id === message.id))
                                return prev;
                            return { ...prev, messages: [...prev.messages, message] };
                        });
                    }
                    patchContext((ctx) => ({
                        ...ctx,
                        conversations: ctx.conversations.map((c) => c.id === message.conversationId
                            ? {
                                ...c,
                                lastMessageAt: message.createdAt,
                                // An omitted text body has no preview to show; keep the last one.
                                lastMessagePreview: bodyOmitted && message.kind === 'text'
                                    ? c.lastMessagePreview
                                    : previewOf(message),
                                unreadCount: isOwn || isOpen || alreadyApplied ? c.unreadCount : c.unreadCount + 1,
                            }
                            : c),
                    }));
                    if (isOpen && !isOwn) {
                        void markTeamConversationRead(message.conversationId).catch(() => { });
                    }
                }
                // Typing implies "no longer typing" once the message lands.
                setTyping((prev) => {
                    const list = prev[message.conversationId];
                    if (!list?.length)
                        return prev;
                    return {
                        ...prev,
                        [message.conversationId]: list.filter((t) => t.userId !== message.senderId),
                    };
                });
            }),
            teamHub.on('messageDeleted', (event) => {
                if (event.replyToId) {
                    // A deleted thread reply: drop it from the thread and decrement the
                    // parent's count in the top-level list.
                    const parentId = event.replyToId;
                    // Idempotent, like the bump: a cached thread that no longer holds the reply has
                    // already had it removed and counted (a `resync` reload). No cached thread: apply.
                    const thread = queryClient.getQueryData(teamThreadKey(parentId));
                    const present = thread ? thread.messages.some((m) => m.id === event.messageId) : true;
                    if (!present)
                        return;
                    queryClient.setQueryData(teamThreadKey(parentId), (prev) => prev
                        ? { ...prev, messages: prev.messages.filter((m) => m.id !== event.messageId) }
                        : prev);
                    queryClient.setQueryData(teamMessagesKey(event.conversationId), (prev) => prev
                        ? {
                            ...prev,
                            messages: prev.messages.map((m) => m.id === parentId ? { ...m, replyCount: Math.max(0, m.replyCount - 1) } : m),
                        }
                        : prev);
                    return;
                }
                // A deleted root, and the replies it took with it. Dropping the root
                // from the page is what closes an open thread view (TeamMessageThread
                // keys the thread on the live page). A root with many replies arrives
                // as several events repeating the root, so every step is a no-op the
                // second time.
                const gone = new Set([event.messageId, ...(event.replyIds ?? [])]);
                queryClient.setQueryData(teamMessagesKey(event.conversationId), (prev) => {
                    if (!prev || !prev.messages.some((m) => gone.has(m.id)))
                        return prev;
                    return { ...prev, messages: prev.messages.filter((m) => !gone.has(m.id)) };
                });
                // A thread may be cached under the root's id or, opened from a reply,
                // under that reply's id.
                for (const id of gone)
                    queryClient.removeQueries({ queryKey: teamThreadKey(id) });
            }),
            teamHub.on('conversationArchived', (event) => {
                // An admin removed the channel: drop it everywhere; close it if open.
                patchContext((ctx) => ({
                    ...ctx,
                    conversations: ctx.conversations.filter((c) => c.id !== event.conversationId),
                }));
                queryClient.removeQueries({ queryKey: teamMessagesKey(event.conversationId) });
                setActiveConversationId((prev) => (prev === event.conversationId ? null : prev));
            }),
            // Same reducer as the optimistic click; the page AND every cached thread.
            teamHub.on('reactionChanged', patchReactionCaches),
            teamHub.on('conversationCreated', adoptConversation),
            teamHub.on('typing', (event) => {
                setTyping((prev) => {
                    const list = (prev[event.conversationId] ?? []).filter((t) => t.userId !== event.userId);
                    return {
                        ...prev,
                        [event.conversationId]: [
                            ...list,
                            {
                                userId: event.userId,
                                displayName: event.displayName,
                                expiresAt: Date.now() + 4000,
                            },
                        ],
                    };
                });
            }),
        ];
        return () => {
            subs.forEach((unsub) => unsub());
            if (caseCountsTimer !== null)
                clearTimeout(caseCountsTimer);
        };
    }, []);
    // Typing entries expire after 4s of silence.
    useEffect(() => {
        const interval = setInterval(() => {
            setTyping((prev) => {
                const now = Date.now();
                let changed = false;
                const next = {};
                for (const [convId, list] of Object.entries(prev)) {
                    const kept = list.filter((t) => t.expiresAt > now);
                    if (kept.length !== list.length)
                        changed = true;
                    if (kept.length > 0)
                        next[convId] = kept;
                }
                return changed ? next : prev;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, []);
    const openConversation = useCallback((conversationId) => {
        setActiveConversationId(conversationId);
        if (conversationId) {
            patchContext((ctx) => ({
                ...ctx,
                conversations: ctx.conversations.map((c) => c.id === conversationId ? { ...c, unreadCount: 0 } : c),
            }));
            void markTeamConversationRead(conversationId).catch(() => { });
        }
    }, []);
    const dmMutation = useMutation({
        mutationFn: (userId) => createTeamDm(userId),
        onSuccess: (conversation) => {
            adoptConversation(conversation);
            setActiveConversationId(conversation.id);
            setPanelOpen(true);
        },
    });
    const channelMutation = useMutation({
        mutationFn: ({ groupId, name }) => createTeamChannel(groupId, name),
        onSuccess: (conversation) => {
            adoptConversation(conversation);
            setActiveConversationId(conversation.id);
        },
    });
    const caseThreadMutation = useMutation({
        mutationFn: (caseId) => getOrCreateCaseThread(caseId),
        onSuccess: (conversation) => {
            // The thread may already exist (and carry unread): open it properly.
            adoptConversation(conversation);
            openConversation(conversation.id);
            setPanelOpen(true);
        },
    });
    // useMutation hands back a new result object every render, but its
    // mutateAsync is the observer's bound method — stable — so depending on it
    // (not the result) keeps these callbacks, and the context value, stable.
    const dmMutateAsync = dmMutation.mutateAsync;
    const openDm = useCallback(async (userId) => {
        await dmMutateAsync(userId);
    }, [dmMutateAsync]);
    const caseThreadMutateAsync = caseThreadMutation.mutateAsync;
    const openCaseThread = useCallback(async (caseId) => {
        await caseThreadMutateAsync(caseId);
    }, [caseThreadMutateAsync]);
    const channelMutateAsync = channelMutation.mutateAsync;
    const createChannel = useCallback(async (groupId, name) => {
        await channelMutateAsync({ groupId, name });
    }, [channelMutateAsync]);
    const setMyStatus = useCallback((status) => {
        // Optimistic write into the presence map itself (the derived source of
        // myStatus); the presenceChanged echo confirms, and the next roster corrects.
        const selfId = selfIdRef.current;
        setPresence((prev) => (selfId ? { ...prev, [selfId]: status } : prev));
        void teamHub.setStatus(status).then((outcome) => {
            if (outcome === 'persisted') {
                persistedStatusRef.current = status;
                return;
            }
            // Not saved, even after the throttle retry: stop showing it (unless the user signed out
            // or someone else signed in meanwhile). A superseded choice leaves the newer one alone.
            if (outcome !== 'failed' || !selfId || selfIdRef.current !== selfId)
                return;
            setPresence((prev) => prev[selfId] === status ? { ...prev, [selfId]: persistedStatusRef.current } : prev);
        });
    }, []);
    const typingIn = useCallback((conversationId) => typing[conversationId] ?? [], [typing]);
    // Opening the panel lands on the conversation that was left open: anything
    // that arrived while the panel was closed counted as unread, so it is read
    // now (and only then — no read call when nothing is unread).
    const openPanel = useCallback((open) => {
        setPanelOpen(open);
        const activeId = activeConvRef.current;
        if (open && activeId)
            markReadIfUnread(activeId);
    }, []);
    const togglePanel = useCallback(() => openPanel(!panelOpenRef.current), [openPanel]);
    const totalUnread = useMemo(() => context.conversations.reduce((sum, c) => sum + c.unreadCount, 0), [context.conversations]);
    const onlineCount = useMemo(() => context.members.filter((m) => m.userId !== context.userId && presence[m.userId]).length, [context.members, context.userId, presence]);
    const value = useMemo(() => ({
        enabled,
        context,
        connectionState,
        presence,
        myStatus,
        setMyStatus,
        panelOpen,
        setPanelOpen: openPanel,
        togglePanel,
        activeConversationId,
        openConversation,
        openDm,
        openCaseThread,
        createChannel,
        typingIn,
        totalUnread,
        onlineCount,
    }), [
        enabled,
        context,
        connectionState,
        presence,
        myStatus,
        setMyStatus,
        panelOpen,
        openPanel,
        togglePanel,
        activeConversationId,
        openConversation,
        openDm,
        openCaseThread,
        createChannel,
        typingIn,
        totalUnread,
        onlineCount,
    ]);
    const canWrite = context.groups.some((g) => g.canWrite);
    const viewerInfo = useMemo(() => ({ enabled, canWrite, userId: context.userId }), [enabled, canWrite, context.userId]);
    return (_jsx(TeamChatViewerContext.Provider, { value: viewerInfo, children: _jsx(TeamChatReactContext.Provider, { value: value, children: children }) }));
}
/** Messages for one conversation: newest page + append-only live updates (hub
 * events patch this cache in the provider). Older pages prepend via loadOlder. */
// eslint-disable-next-line react-refresh/only-export-components
export function useTeamMessages(conversationId) {
    const query = useQuery({
        queryKey: teamMessagesKey(conversationId ?? 'none'),
        queryFn: () => getTeamMessages(conversationId),
        enabled: !!conversationId,
        staleTime: 30_000,
        retry: false,
    });
    const loadOlder = useCallback(async () => {
        if (!conversationId)
            return;
        const current = queryClient.getQueryData(teamMessagesKey(conversationId));
        const oldest = current?.messages[0];
        if (!current || !oldest || !current.hasMore)
            return;
        const older = await getTeamMessages(conversationId, { before: oldest.id });
        queryClient.setQueryData(teamMessagesKey(conversationId), (prev) => prev ? { hasMore: older.hasMore, messages: [...older.messages, ...prev.messages] } : older);
    }, [conversationId]);
    const send = useCallback(async (body, options) => {
        if (!conversationId)
            return;
        const created = await sendTeamMessage(conversationId, body, options);
        // The hub echo also lands; the provider's append dedupes by id.
        queryClient.setQueryData(teamMessagesKey(conversationId), (prev) => {
            if (!prev || prev.messages.some((m) => m.id === created.id))
                return prev;
            return { ...prev, messages: [...prev.messages, created] };
        });
    }, [conversationId]);
    return { query, loadOlder, send };
}
/** One message thread (root + replies). Live replies are appended by the
 * provider's hub handlers; the reply-count bump on the parent rides the hub
 * echo so it increments exactly once for everyone, sender included. */
// eslint-disable-next-line react-refresh/only-export-components
export function useTeamThread(conversationId, parentId) {
    const query = useQuery({
        queryKey: teamThreadKey(parentId ?? 'none'),
        queryFn: () => getTeamThread(parentId),
        enabled: !!parentId,
        staleTime: 15_000,
        retry: false,
    });
    const sendReply = useCallback(async (body) => {
        if (!conversationId || !parentId)
            return;
        const created = await sendTeamMessage(conversationId, body, { replyToId: parentId });
        // The hub echo also lands; both paths dedupe by id.
        queryClient.setQueryData(teamThreadKey(parentId), (prev) => {
            if (!prev || prev.messages.some((m) => m.id === created.id))
                return prev;
            return { ...prev, messages: [...prev.messages, created] };
        });
    }, [conversationId, parentId]);
    return { query, sendReply };
}
